import React, {
  ChangeEvent,
  FC,
  FormEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { CountryCode } from 'libphonenumber-js/types.cjs'
import { Cog, Pencil, UserRound } from 'lucide-react'
import { parsePhoneNumber } from 'react-phone-number-input'
import { toast } from 'sonner'

import Cropper from '@components/settings/Cropper'
import { Button } from '@components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog'
import { Input } from '@components/ui/input'
import { InputPhone } from '@components/ui/input-phone'
import { Label } from '@components/ui/label'
import { useCurrentCountry } from '@hooks/use-current-country'
import { parseErrorMessage } from '@lib/errors'
import {
  issueEmailVerificationChallenge,
  issuePhoneNumberVerificationChallenge,
  updateMe,
  verifyEmailVerificationChallenge,
  verifyPhoneNumberVerificationChallenge,
  whoami,
} from '@gen/api/v1/api-ApiService_connectquery'
import { DialogDescription } from '@radix-ui/react-dialog'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@components/ui/input-otp'
import { formatPhoneNumber } from '@lib/phone-number'
import { Textarea } from '@components/ui/textarea'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@components/ui/card'
import { useAuthenticatedMutation, useSession } from '@context/sessions'
import Title from '@components/title'

const AccountSettingsView: FC = () => {
  const detectedCountry = useCurrentCountry()
  const profilePicureRef = useRef<HTMLInputElement>(null)
  const session = useSession()

  const issueEmailVerificationChallengeMutation = useAuthenticatedMutation(
    issueEmailVerificationChallenge,
  )
  const issuePhoneVerificationChallengeMutation = useAuthenticatedMutation(
    issuePhoneNumberVerificationChallenge,
  )
  const updateMeMutation = useAuthenticatedMutation(updateMe)
  const verifyEmailVerificationChallengeMutation = useAuthenticatedMutation(
    verifyEmailVerificationChallenge,
  )
  const verifyPhoneNumberVerificationChallengeMutation =
    useAuthenticatedMutation(verifyPhoneNumberVerificationChallenge)
  const whoamiMutation = useAuthenticatedMutation(whoami)

  const [bio, setBio] = useState('')
  const [cropImageFileType, setCropImageFileType] = useState('')
  const [cropImageUrl, setCropImageUrl] = useState('')
  const [cropperOpen, setCropperOpen] = useState(false)
  const [currentCountry, setCurrentCountry] = useState<CountryCode>()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [emailVerificationCode, setEmailVerificationCode] = useState('')
  const [emailVerificationOpen, setEmailVerificationOpen] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneNumberVerificationCode, setPhoneNumberVerificationCode] =
    useState('')
  const [phoneNumberVerificationOpen, setPhoneNumberVerificationOpen] =
    useState(false)
  const [profilePictureUrl, setProfilePictureUrl] = useState('')
  const [username, setUsername] = useState('')
  const [website, setWebsite] = useState('')

  const handleBasicInfoSubmit = async (e: FormEvent) => {
    e.preventDefault()

    try {
      await updateMeMutation.mutateAsync({
        bio,
        displayName,
        username,
        website,
      })

      toast.success('Basic info updated')
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to update basic info', {
        description: message,
      })
      return
    }

    try {
      const whoamiRes = await whoamiMutation.mutateAsync({})
      if (whoamiRes) {
        setDisplayName(whoamiRes?.sessionClaims?.user?.displayName || '')
        setUsername(whoamiRes?.sessionClaims?.user?.username || '')
      }
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed refetch user info', {
        description: message,
      })
    }
  }

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault()

    try {
      await issueEmailVerificationChallengeMutation.mutateAsync({
        email,
      })

      toast.success('Email verification sent')
      setEmailVerificationOpen(true)
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to send email verification', {
        description: message,
      })
    }
  }

  const handleEmailVerificationSubmit = async (e: FormEvent) => {
    e.preventDefault()

    try {
      await verifyEmailVerificationChallengeMutation.mutateAsync({
        code: emailVerificationCode,
      })

      toast.success('Email verified')
      setEmailVerificationOpen(false)
      setEmailVerificationCode('')
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to verify email', {
        description: message,
      })
    }

    try {
      const whoamiRes = await whoamiMutation.mutateAsync({})
      if (whoamiRes?.sessionClaims?.user?.email) {
        setEmail(whoamiRes.sessionClaims.user.email)
      }
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to refetch user info', {
        description: message,
      })
    }
  }

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    try {
      if (!e.target.files) {
        setProfilePictureUrl('')
        return
      }

      // Get the raw image base64 string
      const file = e.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = function (e: ProgressEvent<FileReader>) {
          if (!e.target) return

          const base64String = e.target.result
          if (typeof base64String !== 'string') return

          setCropImageFileType(file.type)
          setCropImageUrl(base64String)
          setCropperOpen(true)
        }

        reader.readAsDataURL(file)
      }
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to parse image file', {
        description: message,
      })
    }
  }

  const handlePhoneNumberSubmit = async (e: FormEvent) => {
    e.preventDefault()

    try {
      if (!currentCountry) {
        throw new Error('Could not determine current country')
      }

      const formattedPhoneNumber = formatPhoneNumber(
        currentCountry as CountryCode,
        phoneNumber,
      )

      await issuePhoneVerificationChallengeMutation.mutateAsync({
        phoneNumber: formattedPhoneNumber,
      })

      toast.success('Phone verification sent')
      setPhoneNumberVerificationOpen(true)
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to send phone verification', {
        description: message,
      })
    }
  }

  const handlePhoneNumberVerificationSubmit = async (e: FormEvent) => {
    e.preventDefault()

    try {
      await verifyPhoneNumberVerificationChallengeMutation.mutateAsync({
        code: phoneNumberVerificationCode,
      })

      toast.success('Phone number verified')

      setPhoneNumberVerificationOpen(false)
      setPhoneNumberVerificationCode('')
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to verify phone number', {
        description: message,
      })
    }

    try {
      const whoamiRes = await whoamiMutation.mutateAsync({})
      if (whoamiRes?.sessionClaims?.user?.phoneNumber) {
        const parsedPhoneNumber = parsePhoneNumber(
          whoamiRes.sessionClaims.user.phoneNumber,
          detectedCountry as CountryCode,
        )

        if (parsedPhoneNumber) {
          setPhoneNumber(parsedPhoneNumber.formatNational())

          if (parsedPhoneNumber.country) {
            setCurrentCountry(parsedPhoneNumber.country)
          }
        }
      }
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to refetch user info', {
        description: message,
      })
    }
  }

  const phoneNumbersMatch = (): boolean => {
    const parsedCurrent = parsePhoneNumber(
      session?.user?.phoneNumber || '',
      currentCountry,
    )
    const parsedExisting = parsePhoneNumber(phoneNumber, currentCountry)

    if (!parsedCurrent || !parsedExisting) {
      return false
    }

    return (
      parsedCurrent.formatNational() === parsedExisting.formatNational() &&
      parsedCurrent.country === parsedExisting.country
    )
  }

  useEffect(() => {
    if (session?.user) {
      setBio(session.user.bio || '')
      setEmail(session.user.email || '')
      setDisplayName(session.user.displayName || '')
      setPhoneNumber(session.user.phoneNumber || '')
      setProfilePictureUrl(session.user.profilePictureUrl || '')
      setUsername(session.user.username || '')
      setWebsite(session.user.website || '')

      if (session.user.phoneNumber && detectedCountry) {
        const parsedPhoneNumber = parsePhoneNumber(
          session.user.phoneNumber,
          detectedCountry as CountryCode,
        )

        if (parsedPhoneNumber) {
          setPhoneNumber(parsedPhoneNumber.number)

          parsedPhoneNumber.getPossibleCountries().forEach((country) => {
            if (country === detectedCountry) {
              setCurrentCountry(country as CountryCode)
            }
          })
        }
      }
    }
  }, [detectedCountry, session])

  useEffect(() => {
    if (detectedCountry && !currentCountry) {
      setCurrentCountry(detectedCountry as CountryCode)
    }
  }, [detectedCountry, currentCountry])

  return (
    <div className="max-w-3xl mx-auto pb-16">
      <Title title="Account settings" />

      <div>
        <h1 className="text-3xl font-semibold flex items-center">
          <Cog className="mr-2" strokeWidth={3} />
          Account Settings
        </h1>
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Profile info</CardTitle>
          <CardDescription>
            Manage your profile picture, username, display name, and bio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div>
            <div
              className="cursor-pointer w-48 relative overflow-hidden rounded-full"
              onClick={() => profilePicureRef.current?.click()}
            >
              {profilePictureUrl && profilePictureUrl.length > 0 ? (
                <img
                  className="rounded-full w-48 h-48 object-cover"
                  src={profilePictureUrl}
                />
              ) : (
                <div className="rounded-full w-48 h-48 flex items-center justify-center bg-secondary overflow-hidden">
                  <UserRound className="w-48 h-48 text-black dark:text-white opacity-15" />
                </div>
              )}

              <input
                className="hidden"
                onChange={handleImageChange}
                ref={profilePicureRef}
                type="file"
              />

              {/* Ensure the absolute child is clipped */}
              <div className="absolute bottom-0 w-full bg-black bg-opacity-50 text-xs rounded-b-full">
                <div className="flex items-center justify-center text-white text-center p-2 relative">
                  <Pencil className="w-3 h-3 mr-2" />
                  Edit
                </div>
              </div>
            </div>
          </div>

          <form className="mt-8" onSubmit={handleBasicInfoSubmit}>
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div className="mt-4">
              <Label htmlFor="displayName">Display name</Label>
              <Input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="mt-4">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
                type="url"
                value={website}
              />
            </div>

            <div className="mt-4">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                className="resize-none"
                id="bio"
                maxLength={255}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself"
                value={bio}
              />
            </div>

            <div className="text-right w-full">
              <Button
                className="mt-4 ml-auto"
                disabled={
                  bio === session?.user?.bio &&
                  displayName === session?.user?.displayName &&
                  username === session?.user?.username &&
                  website === session?.user?.website
                }
                type="submit"
              >
                Update
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Contact info</CardTitle>
          <CardDescription>Update your email or phone number</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleEmailSubmit}>
            <Label htmlFor="email">Email</Label>

            <div className="flex items-center">
              <Input
                className="w-full mr-2"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button disabled={email === session?.user?.email} type="submit">
                Change email
              </Button>
            </div>
          </form>

          <form className="mt-8" onSubmit={handlePhoneNumberSubmit}>
            <Label htmlFor="phoneNumber">Phone number</Label>
            <div className="flex items-center">
              <InputPhone
                className="w-full mr-2"
                defaultCountry={currentCountry}
                id="phoneNumber"
                placeholder="Enter a phone number"
                type="tel"
                value={phoneNumber}
                onChange={setPhoneNumber}
              />
              <Button disabled={!phoneNumbersMatch()} type="submit">
                Change phone number
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Dialog open={cropperOpen} onOpenChange={setCropperOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Crop image</DialogTitle>
          </DialogHeader>
          <Cropper
            fileType={cropImageFileType}
            setOpen={setCropperOpen}
            setProfilePictureUrl={setProfilePictureUrl}
            url={cropImageUrl}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={emailVerificationOpen}
        onOpenChange={setEmailVerificationOpen}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Verify email</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              A verification email has been sent to <strong>{email}</strong>.
              Please enter the code in the email below.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEmailVerificationSubmit}>
            <div className="mt-4 w-full flex flex-col justify-center">
              <div className="flex justify-center w-full">
                <InputOTP
                  maxLength={6}
                  onChange={setEmailVerificationCode}
                  value={emailVerificationCode}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                className="mt-4"
                disabled={emailVerificationCode.length < 6}
                type="submit"
              >
                Verify email
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={phoneNumberVerificationOpen}
        onOpenChange={setPhoneNumberVerificationOpen}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader className="w-full">
            <DialogTitle>Verify phone number</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              A verification code has been sent to{' '}
              <strong>{phoneNumber}</strong>. Please enter the code below.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handlePhoneNumberVerificationSubmit}>
            <div className="mt-4 w-full flex flex-col justify-center">
              <div className="flex justify-center w-full">
                <InputOTP
                  maxLength={6}
                  onChange={setPhoneNumberVerificationCode}
                  value={phoneNumberVerificationCode}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                className="mt-4 w-full"
                disabled={phoneNumberVerificationCode.length < 6}
                type="submit"
              >
                Verify phone number
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AccountSettingsView
